import React, { forwardRef, useCallback, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import type { NewIconProps } from '@atlaskit/icon/types';
import Link from '@atlaskit/link';
import {
	TopNavButton,
	TopNavIconButton,
	TopNavLinkButton,
} from '@atlaskit/navigation-system/theming/top-nav-button';
import { useHasCustomTheme } from '@atlaskit/navigation-system/theming/use-has-custom-theme';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	Anchor,
	type AnchorProps,
	Box,
	Flex,
	Pressable,
	type PressableProps,
	Stack,
	Text,
} from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type ACTEvent,
	AdControlDropdownMenu,
	AdControlProvider,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';
import { type TouchpointIntent } from '@atlassian/touchpoint-commons-event-analytics';

import { type LinkId } from '../analytics/types';
import { UiViewedEvent } from '../analytics/ui-viewed-event';
import { useAnalytics } from '../analytics/use-analytics';
import { useOnClickDismissMenu } from '../analytics/use-on-click-dismiss-menu';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { getTrialOfferingName } from '../services/get-trial-offering-name';
import { isEligibleForEAtoOSBExperiment } from '../services/is-eligible-for-ea-to-osb-experiment';
import { isEligibleForEAtoOSBPremiumNonRTExperiment } from '../services/is-eligible-for-ea-to-osb-premium-non-rt-experiment';
import { isEligibleForExploreEditionBenefitsModal } from '../services/is-eligible-for-explore-edition-benefits-modal';
import { type ButtonContent, useButtonContent } from '../services/use-button-content';
import { useTracedUrl } from '../services/use-traced-url';

import { useSpotlight } from './spotlights';

export const EDITION_AWARENESS_SPOTLIGHT_TARGET = 'edition-awareness';

const styles = cssMap({
	button: {
		borderWidth: token('border.width'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		borderStyle: 'solid',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.050'),
		minHeight: '32px',
		color: token('color.text'),
		transitionProperty: 'color, background-color',
		transitionDuration: '200ms',
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		textDecoration: 'none',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
			color: token('color.text'),
		},

		'&:focus': {
			textDecoration: 'none',
			color: token('color.text'),
		},

		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			textDecoration: 'none',
		},
	},
	// platform-dst-shape-theme-default TODO: Merge into button after rollout
	buttonT26Shape: {
		borderRadius: token('radius.medium'),
	},
	default: {
		color: token('color.text.discovery'),
		borderColor: token('color.border.discovery'),

		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
			color: token('color.text.discovery'),
		},

		'&:focus': {
			color: token('color.text.discovery'),
		},

		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
		},
	},
	warning: {
		borderColor: token('color.border.warning'),
	},
	danger: {
		borderColor: token('color.border.danger'),
	},
	icon: {
		display: 'flex',
		// icon size cannot grow and shrink
		flexGrow: 0,
		flexShrink: 0,
		alignSelf: 'center',
		userSelect: 'none',
	},
	dismissible: {
		borderTopRightRadius: '0',
		borderBottomRightRadius: '0',
	},
	dismissMenu: {
		borderWidth: token('border.width'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		borderStyle: 'solid',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.100'),
		paddingBlock: token('space.0'),
		minHeight: '32px',
		color: token('color.text'),
		transitionProperty: 'color, background-color',
		transitionDuration: '200ms',
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		textDecoration: 'none',
		borderColor: token('color.border.discovery'),
		borderLeftWidth: '0',
		// platform-dst-shape-theme-default TODO: Remove these eslint-disables after rollout
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		borderTopLeftRadius: '0',
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		borderBottomLeftRadius: '0',

		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},

		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
		},
	},
	// platform-dst-shape-theme-default TODO: Merge into dismissMenu after rollout
	// platform-dst-shape-theme-default TODO: Remove the eslint-disable after rollout
	dismissMenuT26Shape: {
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		borderRadius: token('radius.medium'),
		borderTopLeftRadius: '0',
		borderBottomLeftRadius: '0',
	},
	reasonsUl: {
		marginTop: '0',
		marginLeft: token('space.200'),
		paddingLeft: token('space.100'),
	},
});

function Icon({
	appearance,
	icon,
	color,
}: {
	appearance?: ButtonContent['appearance'];
	/** Used in the top nav when a customer has a custom theme applied */
	color?: NewIconProps['color'];
	icon: ButtonContent['icon'];
}) {
	if (icon === 'warning') {
		const defaultColor =
			appearance === 'danger' ? token('color.icon.danger') : token('color.icon.warning');

		return <StatusWarningIcon label="" color={color ?? defaultColor} size="small" />;
	}

	if (icon === 'credit-card') {
		return <CreditCardIcon label="" color={color ?? token('color.icon.discovery')} />;
	}

	return <EditionsIcon label="" color={color ?? token('color.icon.discovery')} />;
}

export const EditionAwarenessButton: React.ForwardRefExoticComponent<
	Omit<Omit<PressableProps, 'children'>, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, Omit<PressableProps, 'children'>>(function (
	{ onClick, ...rest },
	ref,
) {
	const buttonContent = useButtonContent();

	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default', isDismissible, isEducationDiscount } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const analyticsAttributes = useMemo(
		() => ({
			type: 'button',
			icon,
			appearance,
			isEducationDiscount,
		}),
		[icon, appearance, isEducationDiscount],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);

			// Fire exposure for ea_to_osb_experiment control tenants
			if (isEligibleForEAtoOSBExperiment(context)) {
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea_to_osb_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
					trackEventParameters: {
						trackEventGrowthInitiative: 'tako',
					},
				});
			}

			// Fire exposure for ea-to-osb-premium-non-rt experiments
			if (isEligibleForEAtoOSBPremiumNonRTExperiment(context)) {
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea-to-osb-premium-non-rt-aa',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
					trackEventParameters: {
						trackEventGrowthInitiative: 'tako',
					},
				});
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea-to-osb-premium-non-rt-ab',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
					trackEventParameters: {
						trackEventGrowthInitiative: 'tako',
					},
				});
			}

			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes, getExperimentValue, context],
	);

	const handleOnMouseEnter = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	const isSTARExperimentVariation =
		isEligibleForExploreEditionBenefitsModal(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'jsmgrowth466_star',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		}) === 'variation';

	const Button = hasCustomTheme ? (
		// When there's a custom theme applied we just return a button which gets styled by the theme
		<TopNavButton
			{...rest}
			ref={ref}
			onClick={handleOnClick}
			onMouseEnter={handleOnMouseEnter}
			iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
		>
			{text}
			<UiViewedEvent
				actionSubjectId="editionAwareness"
				actionSubject="button"
				attributes={analyticsAttributes}
			/>
		</TopNavButton>
	) : (
		<Pressable
			{...rest}
			ref={ref}
			xcss={cx(
				styles.button,
				fg('platform-dst-shape-theme-default') && styles.buttonT26Shape,
				appearance === 'default' && styles.default,
				appearance === 'warning' && styles.warning,
				appearance === 'danger' && styles.danger,
				isDismissible && styles.dismissible,
			)}
			onClick={handleOnClick}
			onMouseEnter={handleOnMouseEnter}
		>
			<Box xcss={styles.icon}>
				<Icon appearance={appearance} icon={icon} />
			</Box>
			{text}
			<UiViewedEvent
				actionSubjectId="editionAwareness"
				actionSubject="button"
				attributes={analyticsAttributes}
			/>
		</Pressable>
	);

	if (isDismissible) {
		if (fg('remove-spotlight-edition-awareness-fg')) {
			return (
				<Flex alignItems="center">
					{Button}
					<DismissMenu />
				</Flex>
			);
		}

		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				<Flex alignItems="center">
					{Button}
					<DismissMenu />
				</Flex>
			</SpotlightTarget>
		);
	}

	if (fg('remove-spotlight-edition-awareness-fg')) {
		return isSTARExperimentVariation ? <div id="edition-awareness-button">{Button}</div> : Button;
	}

	return <SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>{Button}</SpotlightTarget>;
});

export const EditionAwarenessLink: React.ForwardRefExoticComponent<
	Omit<
		Omit<AnchorProps, 'children'> & {
			linkId: LinkId;
			touchpointIntent: TouchpointIntent;
		},
		'ref'
	> &
		React.RefAttributes<HTMLAnchorElement>
> = forwardRef<
	HTMLAnchorElement,
	Omit<AnchorProps, 'children'> & {
		linkId: LinkId;
		touchpointIntent: TouchpointIntent;
	}
>(function ({ onClick, href, touchpointIntent, linkId, ...rest }, ref) {
	const buttonContent = useButtonContent();
	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default' } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const { origin, url: tracedUrl } = useTracedUrl(href);
	if (!tracedUrl) {
		throw new Error('The link url is undefined, please check the context');
	}
	const analyticsAttributes = useMemo(
		() => ({
			type: 'link',
			icon,
			appearance,
			touchpointIntent,
			linkId,
			...origin.toAnalyticsAttributes(),
		}),
		[icon, appearance, touchpointIntent, linkId, origin],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);
			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes],
	);

	const handleOnMouseEnter = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	if (hasCustomTheme) {
		// When there's a custom theme applied we just return a button which gets styled by the theme
		const topNavLinkButton = (
			<TopNavLinkButton
				{...rest}
				href={tracedUrl}
				ref={ref}
				onClick={handleOnClick}
				onMouseEnter={handleOnMouseEnter}
				iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
			>
				{text}
				<UiViewedEvent
					actionSubjectId="editionAwareness"
					actionSubject="button"
					attributes={analyticsAttributes}
				/>
			</TopNavLinkButton>
		);

		if (fg('remove-spotlight-edition-awareness-fg')) {
			return topNavLinkButton;
		}

		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				{topNavLinkButton}
			</SpotlightTarget>
		);
	}

	const anchor = (
		<Anchor
			{...rest}
			href={tracedUrl}
			ref={ref}
			xcss={cx(
				styles.button,
				fg('platform-dst-shape-theme-default') && styles.buttonT26Shape,
				appearance === 'default' && styles.default,
				appearance === 'warning' && styles.warning,
				appearance === 'danger' && styles.danger,
			)}
			onClick={handleOnClick}
			onMouseEnter={handleOnMouseEnter}
		>
			<Box xcss={styles.icon}>
				<Icon appearance={appearance} icon={icon} />
			</Box>
			{text}
			<UiViewedEvent
				actionSubjectId="editionAwareness"
				actionSubject="button"
				attributes={analyticsAttributes}
			/>
		</Anchor>
	);

	if (fg('remove-spotlight-edition-awareness-fg')) {
		return anchor;
	}

	return <SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>{anchor}</SpotlightTarget>;
});

const capitalizeName = (name: string) =>
	name
		.split(' ')
		.map((word) => {
			return word[0].toUpperCase() + word.substring(1);
		})
		.join(' ');

function DismissMenu() {
	const { formatMessage } = useIntl();

	const fireDismissMenuEvent = useOnClickDismissMenu();
	const { product, edition, onDismissButton } = useEditionAwareness();
	const { setActiveSpotlight } = useSpotlight();

	const hasCustomTheme = useHasCustomTheme();

	const trialOfferingName = getTrialOfferingName(product, edition);

	const handleOnEvents = useCallback(
		(e: ACTEvent) => {
			if (e.type === 'DISMISS_SUCCEEDED' && onDismissButton) {
				onDismissButton();
				fireDismissMenuEvent('dismiss');
				setActiveSpotlight('standard-to-premium');
			}

			if (e.type === 'AD_CONTROL_DROP_DOWN_MENU_OPENED') {
				fireDismissMenuEvent('openMenu');
			}

			if (e.type === 'REASONS_BUTTON_CLICKED') {
				fireDismissMenuEvent('learnMore');
			}
		},
		[onDismissButton, setActiveSpotlight, fireDismissMenuEvent],
	);

	const dismissMenuAriaLabel = formatMessage({
		id: '@atlassian/edition-awareness.edition-awareness-dismiss-button.menu-aria',
		defaultMessage: 'More',
		description:
			'The aria label for the dismiss menu, indicating to the user that there are further actions available.',
	});

	const getAdControlsTrigger = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ triggerRef, ...props }: any) => {
			return hasCustomTheme ? (
				<TopNavIconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					aria-label={dismissMenuAriaLabel}
				/>
			) : (
				<Pressable
					{...props}
					ref={triggerRef}
					xcss={cx(
						styles.dismissMenu,
						fg('platform-dst-shape-theme-default') && styles.dismissMenuT26Shape,
					)}
					aria-label={dismissMenuAriaLabel}
				>
					<ShowMoreHorizontalIcon label="" spacing="none" color={token('color.icon.subtlest')} />
				</Pressable>
			);
		},
		[hasCustomTheme, dismissMenuAriaLabel],
	);

	const offering = (
		<Text weight="bold">
			{capitalizeName(product.replace(/-/g, ' '))} {capitalizeName(trialOfferingName)}
		</Text>
	);

	return (
		<AdControlProvider onEvents={(e) => handleOnEvents(e)} shouldShowDismissFlag={false}>
			<AdControlDropdownMenu itemId="default-item" trigger={getAdControlsTrigger}>
				{(props) => (
					<>
						<DismissalButton {...props}>
							<FormattedMessage
								id="@atlassian/edition-awareness.edition-awareness-dismiss-button.dismiss"
								defaultMessage="Dismiss"
								description="An action for the user to permanently dismiss this button to prevent it from appearing again"
							/>
						</DismissalButton>
						<ReasonsButton
							{...props}
							modalProps={{
								body: (
									<Stack space="space.150">
										<Box>
											<FormattedMessage
												id="@atlassian/edition-awareness.edition-awareness-dismiss-button.why-am-i-seeing-this"
												defaultMessage="We think {offering} will improve your team's productivity. This recommendation is only shown to billing admins and is based on:"
												description="A message that informs the user why they are seeing the current plan recommendation for their current product"
												values={{
													offering,
												}}
											/>
										</Box>

										<Stack as="ul" xcss={styles.reasonsUl}>
											<li>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.reason-1"
													defaultMessage="the size of your team"
													description="A reason why the user is seeing the current plan recommendation"
												/>
											</li>
											<li>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.reason-2"
													defaultMessage="your existing Atlassian products"
													description="A reason why the user is seeing the current plan recommendation"
												/>
											</li>
										</Stack>

										<Box>
											<Link
												appearance="default"
												href="https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers"
												target="_blank"
												onClick={() => fireDismissMenuEvent('clickLink')}
											>
												<FormattedMessage
													id="@atlassian/edition-awareness.edition-awareness-dismiss-button.data-privacy.link"
													defaultMessage="Learn more about your data privacy with Atlassian"
													description="Link to Atlassian data privacy page"
												/>
											</Link>
										</Box>
									</Stack>
								),
							}}
						/>
					</>
				)}
			</AdControlDropdownMenu>
		</AdControlProvider>
	);
}
